// What every operation accepts, and how it talks while it works.

/**
 * Progress, as structured events rather than sentences.
 *
 * An operation that called `report('Packaged 1.2 MB')` would still be composing English for a human, which
 * is the thing being moved out of this layer — and a caller rendering progress into a web page or a log line
 * would have to parse it back apart. Each operation declares its own event union instead, and whoever is
 * watching decides what those events look like. `cli/reporter.ts` turns them into the terminal output the
 * CLI has always produced.
 */
export interface OperationOptions<TEvent = never> {
  /**
   * Which project to work on. Defaults to the current directory, because that is what a command means, but
   * a caller driving this from code usually has a project somewhere else in mind.
   */
  cwd?: string
  /** Called as the operation progresses. Omitted means the operation runs silently, which is the default. */
  onEvent?: (event: TEvent) => void
}

/** Narrows `options.onEvent` to something always callable, so operations need no null checks. */
export function emitter<TEvent>(options: OperationOptions<TEvent>): (event: TEvent) => void {
  return options.onEvent ?? (() => undefined)
}
